import { createSelector } from 'reselect';
import {
  Creative,
  CreativeLayer,
  FrameRange,
  Rectangle,
  TextCreativeLayer,
} from 'content-creation-common/models';
import { text as textConstants } from 'content-creation-common/constants';
const { TEXT_TYPE } = textConstants;
import {
  isEmpty as isEmptyObject,
  isEmptySet,
  isNil,
  isNotEmptyString,
  isNotNil,
} from '../../../util/object';
import { isEmpty as isEmptyArray, sortBy } from '../../../util/array';
import { isNumber } from '../../../util/numbers';

import {
  CREATIVE_STATUS_FINALIZED,
  DEFAULT_CREATIVE_HEIGHT_PX,
  DEFAULT_CREATIVE_WIDTH_PX,
  DEFAULT_STAGE_COLOR,
} from '../constants';

import { createKeyedStateSelector } from '../../shared/utils/selectorUtils';

// helper selectors and to avoid circular dependency
const getElementState = (state) => state.elements;
const getContentState = (state) => state.content;
const getMediaState = (state) => state.media;
const getCreativeRootMask = (state) => state.elements.masks.root;
const getFontData = (state) => state.text.fontData;
const getEditingTextContentId = (state) => state.text.isEditingTextId;
const isCreativeLoading = (state) => state.ui.creativeLoading;
const getIsExpandingElementId = (state) => state.ui.isExpandingElementId;
const getAlphaCompositeRulePreview = (state) =>
  state.ui.alphaCompositeRulePreview;
const getEditorSelection = (state) => state.editor.selection;

export const getLoadedCreativeId = (state) => state.creative.loadedCreativeId;
export const getCreativeStatus = (state) => state.creative.creativeStatus;
export const getIsCreativeDesignSwapped = (state) =>
  state.creative.isCreativeDesignSwapped;
export const getCreativeMetadata = (state) => state.creative.metadata;

const getCreativeRootMaskStringProperty = (
  rootMask,
  property,
  defaultValue = null,
) => (isNotEmptyString(rootMask[property]) ? rootMask[property] : defaultValue);

const getCreativeRootMaskNumberProperty = (
  rootMask,
  property,
  defaultValue = null,
) => (isNumber(rootMask[property]) ? rootMask[property] : defaultValue);

export const getCreativeBackgroundColor = createSelector(
  [getCreativeRootMask],
  (creativeRootMask) =>
    getCreativeRootMaskStringProperty(
      creativeRootMask,
      'color',
      DEFAULT_STAGE_COLOR,
    ),
);

export const getCreativeHeight = createSelector(
  [getCreativeRootMask],
  (creativeRootMask) =>
    getCreativeRootMaskNumberProperty(
      creativeRootMask,
      'height',
      DEFAULT_CREATIVE_HEIGHT_PX,
    ),
);

export const getCreativeWidth = createSelector(
  [getCreativeRootMask],
  (creativeRootMask) =>
    getCreativeRootMaskNumberProperty(
      creativeRootMask,
      'width',
      DEFAULT_CREATIVE_WIDTH_PX,
    ),
);

export const getCreativeRectangle = createSelector(
  [getCreativeHeight, getCreativeWidth],
  (creativeHeight, creativeWidth) =>
    new Rectangle(0, 0, creativeWidth, creativeHeight),
);

export const getOutputRangesForBuild = createSelector(
  [getCreativeMetadata],
  (creativeMetadata) => {
    if (isNil(creativeMetadata)) return undefined;
    return creativeMetadata.outputRangesForBuild;
  },
);

export const getMlsMediaIdForPublish = createSelector(
  [getCreativeMetadata],
  (creativeMetadata) => {
    if (isNil(creativeMetadata)) return undefined;
    return creativeMetadata.mlsMediaIdForPublish;
  },
);

export const getAllCreativeBuildErrors = (state) => {
  if (isNil(state.creative)) return undefined;
  return state.creative.creativeBuildErrors;
};

export const getTemplateToImport = (state) => {
  if (isNil(state.creative)) return undefined;
  return state.creative.templateToImport;
};

export const getIsDuplicatingCreative = (state) => {
  if (isNil(state.creative)) return undefined;
  return state.creative.isDuplicatingCreative;
};

export const getIsDuplicatingTemplate = (state) => {
  if (isNil(state.creative)) return undefined;
  return state.creative.isDuplicatingTemplate;
};

export const getIsImportTemplateMode = createSelector(
  [getTemplateToImport],
  (templateToImport) => {
    return isNotNil(templateToImport);
  },
);

export const getAllCreativeBuildErrorsByElementId = createSelector(
  [getAllCreativeBuildErrors],
  (allCreativeBuildErrors) => {
    if (isNil(allCreativeBuildErrors)) return undefined;

    return allCreativeBuildErrors.reduce(
      (creativeBuildErrorsByElementId, creativeBuildError) => {
        creativeBuildErrorsByElementId[
          creativeBuildError.elementId
        ] = creativeBuildError;
        return creativeBuildErrorsByElementId;
      },
      {},
    );
  },
);

export const getCreativeBuildErrorsByElementId = createSelector(
  [getAllCreativeBuildErrorsByElementId, (state, elementId) => elementId],
  (allCreativeBuildErrorsByElementId, elementId) => {
    if (isNil(allCreativeBuildErrorsByElementId)) return undefined;

    return allCreativeBuildErrorsByElementId[elementId];
  },
);

export const getIsCreativeFinalized = createSelector(
  [getCreativeStatus],
  (creativeStatus) => {
    return creativeStatus === CREATIVE_STATUS_FINALIZED;
  },
);

export const getCreativeOutputRanges = createSelector(
  [getCreativeMetadata],
  (metadata) => {
    if (isNil(metadata)) return null;
    return metadata.outputRanges;
  },
);

export const getSortedOutputRanges = createSelector(
  [getCreativeOutputRanges],
  (outputRanges) => {
    if (isEmptyArray(outputRanges)) return null;
    return sortBy(outputRanges, 'start');
  },
);

export const getSortedOutputFrameRanges = createSelector(
  [getSortedOutputRanges],
  (outputRanges) => {
    if (isEmptyArray(outputRanges)) return null;
    return outputRanges.map(({ start, end }) => new FrameRange(start, end));
  },
);

// TODO(lemieux) remove this state out of playback
export const getCreativeMode = (state) => state.playback.creativeMode;

const getLoadingCreative = createSelector(
  [isCreativeLoading, getCreativeBackgroundColor],
  (isLoading, backgroundColor) => {
    if (!isLoading) return null;

    const creative = new Creative();
    creative.backgroundColor = backgroundColor;
    return creative;
  },
);

export const getStateCreative = createSelector(
  [
    getElementState,
    getContentState,
    getMediaState,
    getCreativeMetadata,
    getCreativeMode,
  ],
  (elements, content, media, metadata, creativeMode) => {
    return Creative.fromCreativeDocumentJSON({
      elements,
      content,
      creativeMode,
      media,
      metadata,
    });
  },
);

export const getUICreative = createSelector(
  [
    getLoadingCreative,
    getStateCreative,
    getFontData,
    getEditingTextContentId,
    getIsExpandingElementId,
    getEditorSelection,
    getAlphaCompositeRulePreview,
  ],
  (
    loadingCreative,
    creative,
    fontData,
    editingTextContentId,
    expandingTextElementId,
    editorSelection,
    alphaCompositePreview,
  ) => {
    // return empty creative
    if (Creative.isCreative(loadingCreative)) {
      return loadingCreative;
    }

    // Update text layers
    const textCreativeLayers = Creative.getCreativeLayersByType(
      creative,
      TextCreativeLayer,
    );
    if (!isEmptySet(textCreativeLayers)) {
      const hasFontData = !isEmptyObject(fontData);

      textCreativeLayers.forEach((textLayer) => {
        const { elementId, contentId } = textLayer;

        // if we are editing this layer, remove it from Creative
        // if this is an SVG layer and we are resizing the layer's width, also hide
        if (
          editingTextContentId === contentId ||
          (textLayer.textType === TEXT_TYPE.SVG &&
            expandingTextElementId === elementId)
        ) {
          textLayer.visible = false;
        }

        // update font data
        if (hasFontData) {
          textLayer.fontData = fontData[textLayer.content.font];
        }
      });
    }

    const selectedElementId = isNotNil(editorSelection)
      ? editorSelection.displayElementId
      : null;
    // if user is previewing alpha blend mode, make sure layer is updated correctly
    if (isNotNil(selectedElementId) && isNotNil(alphaCompositePreview)) {
      // get all elementIds
      const selectedLayer = [...creative.layersById.values()].find((layer) => {
        return layer.elementId === selectedElementId;
      });

      if (CreativeLayer.isCreativeLayer(selectedLayer)) {
        selectedLayer.alphaCompositeRule = alphaCompositePreview;
      }
    }

    return creative;
  },
);

/**
 * @function getAllCreativeLayersById
 * @param  {Object} state
 * @return {Map<CreativeLayer>} all layers in the creative
 */
export const getAllCreativeLayersById = createSelector(
  [getStateCreative],
  (creative) => creative.layersById,
);

/**
 * @function getCreativeLayerByElementId
 * @param  {Object} state
 * @return {CreativeLayer}
 */
export const getCreativeLayerByElementId = createKeyedStateSelector(
  [getStateCreative, (state, elementId) => elementId],
  (creative, elementId) => creative.layersById.get(elementId),
);
